export type ChartPoint = { x: number; y: number }

/**
 * Build a rounded SVG path that still passes through every real data point.
 * Catmull–Rom control points provide the soft curve; clamping each control point
 * between its segment endpoints prevents decorative overshoot above/below the
 * measured values.
 */
export function smoothChartPath(points: ChartPoint[]): string {
  if (points.length === 0) return ''
  if (points.length === 1) return `M ${points[0]!.x.toFixed(2)} ${points[0]!.y.toFixed(2)}`

  const commands = [`M ${points[0]!.x.toFixed(2)} ${points[0]!.y.toFixed(2)}`]

  for (let index = 0; index < points.length - 1; index += 1) {
    const previous = points[index - 1] ?? points[index]!
    const current = points[index]!
    const next = points[index + 1]!
    const following = points[index + 2] ?? next
    const minY = Math.min(current.y, next.y)
    const maxY = Math.max(current.y, next.y)
    const clampY = (value: number) => Math.max(minY, Math.min(maxY, value))

    const control1 = {
      x: current.x + (next.x - previous.x) / 6,
      y: clampY(current.y + (next.y - previous.y) / 6),
    }
    const control2 = {
      x: next.x - (following.x - current.x) / 6,
      y: clampY(next.y - (following.y - current.y) / 6),
    }

    commands.push(
      `C ${control1.x.toFixed(2)} ${control1.y.toFixed(2)} ${control2.x.toFixed(2)} ${control2.y.toFixed(2)} ${next.x.toFixed(2)} ${next.y.toFixed(2)}`,
    )
  }

  return commands.join(' ')
}
